import { AsyncPipe } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnInit
} from "@angular/core";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputComponent } from "@components/input/input.component";
import { merge, Observable } from "rxjs";
import { map, tap, withLatestFrom } from "rxjs/operators";
import { InputProcessorType } from "~app/common/components/input/input-processor";
import { SimpleSelectorComponent } from "~app/common/components/simple-selector/simple-selector.component";
import { ErsattningsTyp } from "~app/features/virkeskop/models/utskrift/utskriftValues-models";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { UtskriftMediatorService } from "~app/features/virkeskop/services/utskrift-mediator.service";
import { canEdit } from "~app/role-permissions";
import { VirkeskopValidationRuleDirective } from "../../../validation/directives/virkeskop-validation-rule.directive";
import { VirkeskopValidationMessageComponent } from "../../../validation/validation-message/virkeskop-validation-message.component";
import { KontraktUtskriftForm } from "../kontraktUtskriftForm";
@Component({
    selector: "rask-kontrakt-volym-belopp",
    templateUrl: "./kontrakt-volym-belopp.component.html",
    styleUrls: ["./kontrakt-volym-belopp.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        VirkeskopValidationMessageComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        VirkeskopValidationRuleDirective,
        CheckboxComponent,
        SimpleSelectorComponent,
        AsyncPipe
    ]
})
export class KontraktVolymBeloppComponent implements AfterViewInit, OnInit {

    @Input() formGroup: FormGroup<KontraktUtskriftForm>;
    protected kontraktGuid$: Observable<string>;

    protected readonly beloppRules = [
        "kontraktsbeloppsaknas",
        "betalbeloppstorreankontraktbelopp"
    ];

    protected readonly ersattningstypRules = ["ersattningtypsaknas"];

    protected readonly allRules = [
        ...this.beloppRules,
        ...this.ersattningstypRules
    ];

    protected krPerInmattSelectorOptions = [
        { text: ErsattningsTyp.Nettovolym,
            value: ErsattningsTyp.Nettovolym },
        { text: ErsattningsTyp.Bruttovolym,
            value: ErsattningsTyp.Bruttovolym }
    ];

    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly utskriftMediatorService = inject(UtskriftMediatorService);

    private readonly canEdit = canEdit();

    ngOnInit(): void {
        const kontraktUpdate$ = this.kontraktMediatorService.kontraktUpdate$.pipe(
            tap(kontrakt => {
                this.setDefaultBruttovolymIfSkordarmatning(kontrakt.skordarmatning);
            })
        );

        this.kontraktGuid$ = merge(this.kontraktMediatorService.kontrakt$, kontraktUpdate$).pipe(
            withLatestFrom(this.kontraktMediatorService.isReadonly$.pipe(
                map(readonly => readonly || !this.canEdit())
            )),
            tap(([kontrakt, isReadonly]) => {
                if (kontrakt.skordarmatning || isReadonly) {
                    this.formGroup.controls.ersattningstyp.disable({ emitEvent: false });
                } else {
                    this.formGroup.controls.ersattningstyp.enable({ emitEvent: false });
                }
            }),
            map(([kontrakt, _]) => kontrakt.guid)
        );

        this.setDefaultBruttovolymIfSkordarmatning(this.kontraktMediatorService.kontrakt.skordarmatning);
    }

    ngAfterViewInit(): void {
        const form = this.formGroup.get("visaKontraktsbeloppIUtskrift");
        this.utskriftMediatorService.inkluderaBeloppIUtskrift$.next(form.value);
    }

    protected get seIntProcessor() {
        return InputProcessorType.seLocaleIntProcessor;
    }

    protected onInkluderaIUtskriftChange(checked: boolean) {
        this.utskriftMediatorService.inkluderaBeloppIUtskrift$.next(checked);
    }

    private setDefaultBruttovolymIfSkordarmatning(skordarmatning?: boolean) {
        const ersattningstyp = this.formGroup.controls.ersattningstyp;
        if (skordarmatning && ersattningstyp.value !== ErsattningsTyp.Bruttovolym) {
            ersattningstyp.setValue(ErsattningsTyp.Bruttovolym);
        }
    }
}
